import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Textarea } from "@runcheetah/ui";

type SaveState = "idle" | "saving" | "saved" | "error";

export type EditableTextareaProps = {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  onSave: (v: string) => Promise<unknown> | unknown;
  placeholder?: string;
  rows?: number;
};

export function EditableTextarea({
  label,
  description,
  value,
  onChange,
  onSave,
  placeholder,
  rows = 4,
}: EditableTextareaProps) {
  const intl = useIntl();
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const handleFocus = () => {
    setDraft(value);
    setFocused(true);
    setSaveState("idle");
  };

  const handleSave = async () => {
    onChange(draft);
    setSaveState("saving");

    try {
      await onSave(draft);
      setSaveState("saved");
      setFocused(false);
    } catch {
      setSaveState("error");
    }
  };

  const handleCancel = () => {
    setDraft(value);
    setFocused(false);
    setSaveState("idle");
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description ? (
          <p className="text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <Textarea
        value={focused ? draft : value}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={handleFocus}
        rows={rows}
        className="min-h-24 resize-y text-sm"
        placeholder={
          placeholder ??
          intl.formatMessage(
            { id: "editableTextarea.placeholder", defaultMessage: "Enter {label}..." },
            { label: label.toLowerCase() },
          )
        }
      />
      {focused && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={handleSave} disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <FormattedMessage id="common.saving" defaultMessage="Saving..." />
            ) : (
              <FormattedMessage id="common.save" defaultMessage="Save" />
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        </div>
      )}
      {saveState !== "idle" ? (
        <p className="text-xs text-muted-foreground">
          {saveState === "saved" ? (
            <FormattedMessage id="common.saved" defaultMessage="Saved" />
          ) : null}
          {saveState === "error" ? (
            <FormattedMessage
              id="common.saveErrorTryAgain"
              defaultMessage="Could not save. Try again."
            />
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
